/**
 * Дерево сущностей для рендера превью. 05-ui.md §3.1.
 *
 * sortEntities и priorityOf НЕ определяются здесь заново — они импортируются из entity.ts.
 * Это требование §Хребет данных 3.2: функция сортировки физически одна на кодировщик и на
 * превью, и обязательный тест утверждает `assert.strictEqual` на самом объекте функции,
 * а не на двух одинаково написанных реализациях.
 *
 * Инвариант канонической формы: buildTree бросает NestingError при частичном пересечении.
 * Это не ограничение, а проверка — после resplitEntities пересечений быть не может по
 * построению. Если NestingError вылетел, сломана канонизация, и лучше увидеть это здесь,
 * чем в канале.
 */

import { sortEntities } from './entity.ts';

export interface PreviewEntity {
  type: string;
  offset: number;
  length: number;
  url?: string | undefined;
  language?: string | undefined;
  user?: { id: number } | undefined;
}

export type PreviewNode =
  | { kind: 'text'; text: string }
  | { kind: 'entity'; entity: PreviewEntity; children: PreviewNode[] };

export class NestingError extends Error {
  override readonly name = 'NestingError';
}
export class SurrogateError extends Error {
  override readonly name = 'SurrogateError';
}

const isLowSurrogate = (cu: number): boolean => cu >= 0xdc00 && cu <= 0xdfff;

/**
 * Вызывается ДО построения дерева. Граница сущности внутри суррогатной пары — жёсткая 400
 * от сервера, причём уже после того, как человек утвердил пост (telegram.md §4.4).
 */
export function assertNoSurrogateSplit(text: string, entities: readonly PreviewEntity[]): void {
  for (const e of entities) {
    for (const p of [e.offset, e.offset + e.length]) {
      if (p > 0 && p < text.length && isLowSurrogate(text.charCodeAt(p))) {
        throw new SurrogateError(`entity ${e.type} boundary at ${p} splits a surrogate pair`);
      }
    }
  }
}

export function buildTree(text: string, entities: readonly PreviewEntity[]): PreviewNode[] {
  const sorted = sortEntities(entities);
  assertNoSurrogateSplit(text, sorted);
  let i = 0;

  const build = (from: number, to: number): PreviewNode[] => {
    const out: PreviewNode[] = [];
    let cursor = from;
    while (i < sorted.length && (sorted[i] as PreviewEntity).offset < to) {
      const e = sorted[i] as PreviewEntity;
      const end = e.offset + e.length;
      if (e.offset < cursor) {
        throw new NestingError(`entity ${e.type}@${e.offset} overlaps previous sibling`);
      }
      if (end > to) {
        throw new NestingError(`entity ${e.type}@${e.offset} crosses parent boundary`);
      }
      if (e.offset > cursor) out.push({ kind: 'text', text: text.slice(cursor, e.offset) });
      i++;
      const children = build(e.offset, end);
      out.push({ kind: 'entity', entity: e, children });
      cursor = end;
    }
    if (cursor < to) out.push({ kind: 'text', text: text.slice(cursor, to) });
    return out;
  };

  const res = build(0, text.length);
  if (i !== sorted.length) throw new NestingError('unconsumed entities');
  return res;
}

/** Отладочная форма дерева. Только для тестов и логов. */
export function toDebugString(nodes: readonly PreviewNode[]): string {
  return nodes
    .map((n) =>
      n.kind === 'text'
        ? n.text
        : `[${n.entity.type}${n.entity.url !== undefined ? `:${n.entity.url}` : ''}${toDebugString(n.children)}]`,
    )
    .join('');
}
